import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { SearchableDropdownOption } from '../index';
import { Listbox } from './Listbox';

interface DropdownPanelProps {
  isShown: boolean;
  position: 'left' | 'center' | 'right';
  hideSearch: boolean;
  placeholder: string;
  options: SearchableDropdownOption[];
  selectedValue: string | string[] | null;
  isMulti: boolean;
  onOptionClick: (value: string, label: string) => void;
  onClose: () => void;
  isLoading?: boolean;
  error?: string | Error | null;
  onSearchChange?: (value: string) => void;
  searchValueExternallyControlled?: string;
}

export function DropdownPanel({
  isShown,
  position,
  hideSearch,
  placeholder,
  options,
  selectedValue,
  isMulti,
  onOptionClick,
  onClose,
  isLoading,
  error,
  onSearchChange,
  searchValueExternallyControlled,
}: DropdownPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const [internalSearch, setInternalSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const currentSearchValue = onSearchChange
    ? searchValueExternallyControlled || ''
    : internalSearch;

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(currentSearchValue.toLowerCase()),
  );

  // Reset state and focus when panel opens
  useEffect(() => {
    if (isShown) {
      setHighlightedIndex(-1);
      if (!onSearchChange) {
        setInternalSearch('');
      }
      // Focus listbox when search is hidden (input has autoFocus when visible)
      if (hideSearch) {
        listboxRef.current?.focus();
      }
    }
  }, [isShown, onSearchChange, hideSearch]);

  // Reset highlighted index when selected value changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [selectedValue]);

  const handleSearchChange = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearch(value);
    }
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ') {
      e.stopPropagation();
      return;
    }
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const total = filteredOptions.length;
      if (total > 0) {
        setHighlightedIndex(i => (i + 1) % total);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const total = filteredOptions.length;
      if (total > 0) {
        setHighlightedIndex(i => (i - 1 + total) % total);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        const option = filteredOptions[highlightedIndex];
        onOptionClick(option.value, option.label);
      }
    }
  };

  // Focus listbox when navigating with keyboard
  useEffect(() => {
    if (
      highlightedIndex !== -1 &&
      document.activeElement === inputRef.current &&
      document.activeElement !== listboxRef.current
    ) {
      listboxRef.current?.focus();
    }
  }, [highlightedIndex]);

  if (!isShown) return null;

  return (
    <div
      className={clsx(
        'absolute top-full z-20 mt-1 w-72 rounded-md border border-gray-200 bg-white p-4 shadow-lg',
        {
          'left-0': position === 'left',
          'left-1/2 -translate-x-1/2 transform': position === 'center',
          'right-0': position === 'right',
        },
      )}
    >
      <div className="flex flex-col gap-1">
        {!hideSearch && (
          <>
            <input
              ref={inputRef}
              type="text"
              className="focus:border-fiona mb-2 w-full rounded border border-gray-300 px-2 py-1 text-sm focus:shadow-[0_0_0_2px_theme(colors.fiona)] focus:outline-none"
              placeholder={placeholder}
              value={currentSearchValue}
              onChange={e => handleSearchChange(e.target.value)}
              autoFocus
              onKeyDown={handleKeyDown}
            />
            <hr className="py-1" />
          </>
        )}
        <Listbox
          ref={listboxRef}
          options={filteredOptions}
          selectedValue={selectedValue}
          isMulti={isMulti}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          onOptionClick={onOptionClick}
          isLoading={isLoading}
          error={error}
          searchValue={currentSearchValue}
          hasFilteredOptions={filteredOptions.length > 0}
        />
      </div>
    </div>
  );
}
